import React, { ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import { Schema } from '../../hooks/useSchema';
import { FormControlsForFields, MaybeFieldDefinition } from '../schemed';
import { Button, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { PseudoLink2 } from './Common.styles';
import { generateCode } from '../../api/data';
import { Dialog } from './Dialogs';
import { FormattedMessage } from 'react-intl';
import { FormGrid } from './Forms';
import { MoreActionsMenuButton } from './MoreActionsMenuButton';
import isHotkey from 'is-hotkey';

const MultiAttributeEditorInner = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 10px;
`;

const MultiAttributeEditorWrapper = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;

  gap: 0.25rem;
  padding-right: 30px;

  position: relative;

  min-height: 2.8125rem;

  & .item {
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-thickness: 0.5px;
    text-underline-offset: 2px;
  }

  & .add {
    display: flex;
    align-items: center;
    gap: 1px;
    text-transform: lowercase;
    font-size: 0.9rem;

    & .MuiSvgIcon-root {
      font-size: 0.75rem;
    }

    & .label {
    }
    
  }

  & .actions {
    position: absolute;
    right: 0;
    bottom: 0;
  }
`;

export const copyItemsToClipboard = (items: any[]) => {
  if(!items || !items.length) {
    return;
  }
  const copy = items.map(it => ({ ...it, _id: generateCode() }));
  const text = JSON.stringify({ _tl_items_copy: copy });
  navigator.clipboard.writeText(text);
}

export const getItemsFromClipboard = (): Promise<any[] | null> => {
  return navigator.clipboard.readText()
    .then(text => {
      if(text.includes("_tl_items_copy")) {
        const data = JSON.parse(text);
        const items = data._tl_items_copy as any[];
        if(items) {
          items.map(item => ({ ...item, _id: generateCode()}));
          return items;
        }
      }
      return null;
    });
}


interface Props {
  label?: ReactNode;
  displayText: (item: any) => string;
  value: any[] | undefined | null;
  update: (v: any[] | null) => void;
  schema: Schema;
  fields: MaybeFieldDefinition[];
  disabled?: boolean;
  dflt?: any;
}

export const SimpleMultiAttributeEditor = (props: Props) => {
  const { value, update, displayText } = props;
  const [editItem, setEditItem] = useState<{ item: any, idx: number, isNew?: boolean } | null>(null);

  const saveEditing = () => {
    if(props.disabled || !editItem) {
    }
    else if(editItem.isNew) {
      update([...(value || []), editItem.item]);
    } else {
      update((value || []).map((other,i) => {
        if(editItem.item._id) {
          return editItem.item._id === other._id ? editItem.item : other;
        } else {
          return i === editItem.idx ? editItem.item : other;
        }
      }));
    }
    setEditItem(null);
  }

  const removeEditing = () => {
    if(props.disabled || !editItem || editItem.isNew) {
    } else {
      update((value || []).filter((other,i) => {
        if(editItem.item._id) {
          return editItem.item._id !== other._id;
        } else {
          return i !== editItem.idx;
        }
      }))
    }
    setEditItem(null);
  }

  return (
    <>
      <MultiAttributeEditorWrapper>
        <Typography variant="caption" color="textSecondary">{props.label}</Typography>

        <MultiAttributeEditorInner>
          {(value || []).map((item,i) => (
            <PseudoLink2 className="item" key={`${item._id}`} onClick={() => setEditItem({ item: { ...item }, idx: i })}>
              {displayText(item) || "..."}
            </PseudoLink2>))}

          {!props.disabled &&
            <PseudoLink2 key="add" className="add" onClick={() => setEditItem({ item: { ...(props.dflt || {}), _id: generateCode()}, idx: -1, isNew: true })}>
              <Add />
              <span className="label">
                <FormattedMessage id="common.add" />
              </span>
            </PseudoLink2>}
        </MultiAttributeEditorInner>

        <MoreActionsMenuButton
          strippedButton
          className="actions"
          actions={[
            ["copy", <FormattedMessage id="common.copy" />, () => copyItemsToClipboard(value || [])],
            props.disabled ? null : ["paste", <FormattedMessage id="common.paste" />, () => getItemsFromClipboard().then(newItems => newItems ? update([ ...(value || []), ...newItems ]) : null)],
            props.disabled ? null : ["clear", <FormattedMessage id="common.clear" />, () => update([])],
          ]}
          />
        
      </MultiAttributeEditorWrapper>
      
      <Dialog
        isOpen={!!editItem}
        close={() => setEditItem(null)}
        dialogTitle={props.label}
        noFullscreen
        onKeyUp={e => {
          if(isHotkey("mod+enter", e)) {
            saveEditing();
          }
        }}
        actions={<>
          {!props.disabled && !editItem?.isNew && <Button size="small" onClick={() => removeEditing()}>
            <FormattedMessage id="common.delete" />
          </Button>}
          <Button size="small" color="primary" onClick={() => saveEditing()}>
            <FormattedMessage id="common.ok" />
          </Button>
        </>}>

        <FormGrid columns="1fr">
          <FormControlsForFields
            data={editItem?.item}
            onChange={(o,c) => setEditItem(x => x ? { ...x, item: { ...x.item, ...c}} : x)}
            fields={props.fields}
            schema={props.schema}
            disabled={props.disabled}
            />
        </FormGrid>
      </Dialog>
    </>
  );
}
